import { useLingui } from '@lingui/react/macro'
import type { PaginationState } from '@tanstack/react-table'
import { Box } from 'designSystem/components/Box'
import { Skeleton } from 'designSystem/components/Skeleton'
import { useIsEmbeddedMobileLayout } from 'features/embeddedSidebar/hooks/useIsEmbeddedMobileLayout'
import { DocumentCardResultCountLabel } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/DocumentCardResultCountLabel'
import { PaginationControls } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/PaginationControls'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useDocumentCardPagination } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentCardPagination'
import { SyncedTableHorizontalScrollbar } from 'features/knowledgeDocumentViewer/components/table/SyncedTableHorizontalScrollbar'
import type { ViewMode } from 'features/knowledgeDocumentViewer/types'
import type { ReactElement, RefObject } from 'react'
import { memo, useCallback } from 'react'
import styled from 'styled-components'

import {
  getClientCardFooterPagination,
  getServerPagedCardFooterPagination,
  getTableFooterPagination,
} from './documentResultsFooterPagination'
import { DocumentViewModeToggle } from './DocumentViewModeToggle'

const TABLE_VIEWPORT_SELECTOR = '.rt-ScrollAreaViewport'

const TableScrollbarContainer = styled.div<{ $widthPercent: number }>`
  width: ${({ $widthPercent }) => `${$widthPercent}%`};
  max-width: 100%;
  min-width: 0;
`

const DocumentResultsFooterLoadingSkeleton = memo(
  function DocumentResultsFooterLoadingSkeleton(): ReactElement {
    const { t } = useLingui()
    return (
      <Box
        aria-label={t`Loading document result controls`}
        role='status'
        display='flex'
        align='center'
        justify='between'
        gap='sm'
        width='full'
        shrink={false}
        overflow='hidden'
        border='top'
        padding='sm'>
        <Skeleton
          variant='ditheredPulse'
          style={{ height: '2.25rem', width: '5.5rem' }}
        />
        <Skeleton
          variant='ditheredPulse'
          style={{
            height: '2.25rem',
            maxWidth: '40%',
            width: '16rem',
          }}
        />
      </Box>
    )
  }
)

interface DocumentResultsFooterProps {
  activeIndex: number
  documentTotalCount: number
  filteredDocuments: DocumentTableRow[]
  hasActiveFilters: boolean
  pagination: PaginationState
  setActiveIndex: (index: number) => void
  setPagination: (
    updater: PaginationState | ((prev: PaginationState) => PaginationState)
  ) => void
  tableScrollbarWidthPercent?: number
  tableFilteredCount: number
  tableHasActiveFilters: boolean
  tableScrollContainerRef?: RefObject<HTMLElement | null>
  viewMode: ViewMode
  onViewModeChange: (value: string | number) => void
  isLoading?: boolean
  isServerPaged?: boolean
}

export function DocumentResultsFooter(
  props: DocumentResultsFooterProps
): ReactElement {
  if (props.isLoading) {
    return <DocumentResultsFooterLoadingSkeleton />
  }
  return <LoadedDocumentResultsFooter {...props} />
}

function resolveFooterPaginationProps(args: {
  cardPagination: ReturnType<typeof useDocumentCardPagination>
  documentTotalCount: number
  filteredCount: number
  handleServerPageChange: (page: number) => void
  hasActiveFilters: boolean
  isServerPaged: boolean
  pageIndex: number
  pageSize: number
  tableFilteredCount: number
  tableHasActiveFilters: boolean
  viewMode: ViewMode
}) {
  if (args.viewMode === 'card' && !args.isServerPaged) {
    return {
      ...getClientCardFooterPagination({
        currentPage: args.cardPagination.currentPage,
        displayCount: args.cardPagination.displayCount,
        filteredCount: args.filteredCount,
        hasActiveFilters: args.hasActiveFilters,
        pageEnd: args.cardPagination.pageEnd,
        pageStart: args.cardPagination.pageStart,
        totalPages: args.cardPagination.totalPages,
      }),
      onPageChange: args.cardPagination.handlePageChange,
    }
  }
  if (args.viewMode === 'card') {
    return {
      ...getServerPagedCardFooterPagination({
        hasActiveFilters: args.hasActiveFilters,
        pageIndex: args.pageIndex,
        pageSize: args.pageSize,
        totalCount: args.documentTotalCount,
      }),
      onPageChange: args.handleServerPageChange,
    }
  }
  return {
    ...getTableFooterPagination({
      filteredCount: args.tableFilteredCount,
      hasActiveFilters: args.tableHasActiveFilters,
      isServerPaged: args.isServerPaged,
      pageIndex: args.pageIndex,
      pageSize: args.pageSize,
      totalCount: args.documentTotalCount,
    }),
    onPageChange: args.handleServerPageChange,
  }
}

function LoadedDocumentResultsFooter({
  activeIndex,
  documentTotalCount,
  filteredDocuments,
  hasActiveFilters,
  pagination,
  setActiveIndex,
  setPagination,
  tableScrollbarWidthPercent = 100,
  tableFilteredCount,
  tableHasActiveFilters,
  tableScrollContainerRef,
  viewMode,
  onViewModeChange,
  isServerPaged = false,
}: DocumentResultsFooterProps): ReactElement {
  const isCompact = useIsEmbeddedMobileLayout()
  const cardPagination = useDocumentCardPagination({
    filteredDocuments,
    totalCount: documentTotalCount,
    filteredCount: filteredDocuments.length,
    hasActiveFilters,
    activeIndex,
    onActiveIndexChange: setActiveIndex,
  })

  const handleServerPageChange = useCallback(
    (page: number) => {
      setPagination((prev) => ({ ...prev, pageIndex: page }))
      if (isServerPaged) {
        setActiveIndex(0)
      }
    },
    [isServerPaged, setActiveIndex, setPagination]
  )
  const getTableViewport = useCallback(
    () =>
      tableScrollContainerRef?.current?.querySelector<HTMLElement>(
        TABLE_VIEWPORT_SELECTOR
      ),
    [tableScrollContainerRef]
  )

  const paginationProps = resolveFooterPaginationProps({
    cardPagination,
    documentTotalCount,
    filteredCount: filteredDocuments.length,
    handleServerPageChange,
    hasActiveFilters,
    isServerPaged,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    tableFilteredCount,
    tableHasActiveFilters,
    viewMode,
  })

  return (
    <Box shrink={false} width='full'>
      {viewMode === 'table' && tableScrollContainerRef && (
        <TableScrollbarContainer $widthPercent={tableScrollbarWidthPercent}>
          <SyncedTableHorizontalScrollbar
            enabled
            getViewport={getTableViewport}
          />
        </TableScrollbarContainer>
      )}
      <PaginationControls
        compact={isCompact}
        currentPage={paginationProps.currentPage}
        resultCountLabel={
          <DocumentCardResultCountLabel
            displayCount={paginationProps.displayCount}
            hasActiveFilters={paginationProps.hasActiveFilters}
            pageEnd={paginationProps.pageEnd}
            pageStart={paginationProps.pageStart}
            resultCount={paginationProps.resultCount}
            totalCount={documentTotalCount}
          />
        }
        totalPages={paginationProps.totalPages}
        rightContent={
          <DocumentViewModeToggle
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
          />
        }
        onPageChange={paginationProps.onPageChange}
      />
    </Box>
  )
}
